import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageShell } from '@/components/layout/PageShell';
import { PinkDot } from '@/components/brand/PinkDot';
import { Icon } from '@/components/ui/Icon';
import { StatusDot } from '@/components/ui/StatusDot';
import { EditorialImage } from '@/components/editorial/EditorialImage';
import { FeedItemCard } from '@/components/feed/FeedItemCard';
import { MobileSwipeFeed } from '@/components/feed/MobileSwipeFeed';
import { ROUTES } from '@/config/routes';
import {
  FilterSheet,
  DEFAULT_FILTERS,
  type FeedFilters,
} from '@/components/sheets/FilterSheet';
import { scoreSubjects } from '@/modules/heat/scoring';
import { capture, peek } from '@/modules/heat/signals';
import { useFeedPreferences } from '../hooks/useFeedPreferences';
import { useProfiles } from '@/modules/profiles/useProfiles';
import { composeFeed } from '@/modules/feed/composer';
import { cn } from '@/lib/cn';
import type { CardFace } from '@/components/feed/FeedCard';

/**
 * Feed — mixed cross-surface discovery.
 *
 *   .app profiles (primary) interleaved with cross-promo cards every ~4
 *   items: .vip directory members + .tv series + .tv creators. Each
 *   cross-promo card carries a surface badge so the user knows the tap
 *   takes them elsewhere.
 *
 * Mobile (< md): full-screen TikTok-style vertical swipe feed. Each item
 * fills the viewport; right-side action rail; bottom info column.
 *
 * Desktop (md+): grid of mixed cards. 1 / 2 / 3 columns across breakpoints.
 * The featured-profile hero from the previous round stays at the top, with
 * the stats/photo toggle and distance/presence filters underneath.
 */
export const FeedPage = () => {
  const [filters, setFilters] = useState<FeedFilters>(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const { cardFace, setCardFace } = useFeedPreferences();
  const { profiles, loading } = useProfiles();

  useEffect(() => {
    capture('surface_enter', 'feed');
  }, []);

  // Featured profile for the hero — random per page load, prefers live.
  const featured = useMemo(() => {
    const live = profiles.filter((p) => p.presence === 'live');
    const pool = live.length > 0 ? live : profiles;
    return pool[Math.floor(Math.random() * pool.length)] ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles.length]);

  // Sort + filter the .app profiles before composing.
  // Heat scoring reads the live signal buffer captured via `capture()` —
  // no mock seed. Empty buffer means equal-zero scores, which falls
  // through to the secondary sort keys (band, presence, distance).
  const sortedProfiles = useMemo(() => {
    const scoreMap = scoreSubjects(peek());
    const order = { pulse: 4, hot: 3, warm: 2, cool: 1 } as const;
    const needle = filters.query.trim().toLowerCase();
    const haystack = (p: (typeof profiles)[number]) =>
      [p.handle, p.location, p.mood, p.bio, ...(p.interests ?? [])]
        .join(' ')
        .toLowerCase();

    return [...profiles]
      .filter((p) => parseFloat(p.distance) <= filters.distanceKm)
      .filter((p) => {
        if (filters.presence === 'any') return true;
        if (filters.presence === 'live') return p.presence === 'live';
        if (filters.presence === 'recent') return p.presence !== 'idle';
        return true;
      })
      .filter((p) => filters.bands.includes(p.band))
      .filter((p) => needle.length === 0 || haystack(p).includes(needle))
      .sort((a, b) => {
        const sa = scoreMap.get(a.id)?.value ?? 0;
        const sb = scoreMap.get(b.id)?.value ?? 0;
        if (order[b.band] !== order[a.band]) return order[b.band] - order[a.band];
        if (sb !== sa) return sb - sa;
        if (a.presence !== b.presence) return a.presence === 'live' ? -1 : 1;
        return parseFloat(a.distance) - parseFloat(b.distance);
      });
  }, [filters, profiles]);

  // Compose the mixed feed once per data-shape change.
  // Cross-promo (.vip / .tv) is wired through the composer but currently
  // fed empty arrays — those surfaces have no production DB tables yet,
  // and the feed must show real data only. Re-wire when vip_providers /
  // tv_series / tv_creators ship.
  const items = useMemo(
    () =>
      composeFeed({
        profiles: sortedProfiles,
        vipProfiles: [],
        tvSeries: [],
        tvCreators: [],
        cadence: 4,
        maxPromos: 8,
      }),
    [sortedProfiles],
  );

  const activeFilterCount =
    (filters.distanceKm !== DEFAULT_FILTERS.distanceKm ? 1 : 0) +
    (filters.presence !== DEFAULT_FILTERS.presence ? 1 : 0) +
    (filters.bands.length !== DEFAULT_FILTERS.bands.length ? 1 : 0) +
    (filters.query.trim().length > 0 ? 1 : 0);

  return (
    <PageShell
      mobileTopBar={
        <>
          {/* No wordmark here — PageShell's bar carries the WorldSwitcher in
              its place. The live dot stays: it is status, not identity. */}
          <PinkDot pulse size="h-1.5 w-1.5" />
          <button
            aria-label="Filters"
            onClick={() => setFilterOpen(true)}
            className="relative flex h-9 w-9 items-center justify-center text-text-secondary transition-colors duration-fast ease-signal hover:text-pink"
          >
            <Icon name="tune" size={20} />
            {activeFilterCount > 0 && (
              <span
                aria-hidden
                className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-pink shadow-glow-soft"
              />
            )}
          </button>
        </>
      }
    >
      {/* DESKTOP HERO — image-led featured profile */}
      {featured && (
        <section
          className="relative isolate hidden w-full overflow-hidden border-b border-white/[0.06] md:block"
          style={{ minHeight: 'min(70dvh, 720px)' }}
        >
          <div aria-hidden className="absolute inset-0">
            <EditorialImage
              scene={featured.scene}
              seed={featured.sceneSeed}
              photoUrl={featured.photos[0]}
              aspect="aspect-auto"
              className="h-full"
            />
          </div>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(5,6,8,0.15) 0%, rgba(5,6,8,0) 30%, rgba(5,6,8,0.25) 55%, rgba(5,6,8,0.92) 100%)',
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
            className="relative z-10 grid h-full"
            style={{ minHeight: 'min(70dvh, 720px)' }}
          >
            <div className="flex flex-col justify-end px-12 pb-14 pt-20">
              <Link
                to={ROUTES.profile(featured.handle)}
                className="inline-flex max-w-fit items-center gap-2.5 border border-white/[0.10] bg-ink/55 px-3 py-1.5 backdrop-blur transition-colors duration-fast ease-signal hover:border-pink/40"
              >
                {featured.presence === 'live' && (
                  <StatusDot status="live" size="h-1.5 w-1.5" />
                )}
                <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-secondary">
                  Tonight&apos;s cover
                </span>
                <span aria-hidden className="text-pink">
                  ·
                </span>
                <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-primary">
                  @{featured.handle}
                </span>
              </Link>
              <h1
                className="mt-5 font-display font-black uppercase tracking-display"
                style={{
                  fontSize: 'clamp(48px, 9vw, 168px)',
                  lineHeight: 0.84,
                  letterSpacing: '-0.05em',
                }}
              >
                Find what
                <br />
                <span className="font-light italic text-text-secondary">pulls</span>{' '}
                you<span className="text-pink">.</span>
              </h1>
              <p className="mt-5 max-w-xl text-sm text-text-secondary md:text-base">
                <span className="text-text-primary">{featured.mood}</span>
                <span aria-hidden className="mx-2 text-pink">
                  ·
                </span>
                {featured.location} · {featured.distance}
              </p>
            </div>
          </motion.div>
        </section>
      )}

      {/* CONTROL STRIP — face toggle + presence/filter trigger.
          Visible on both mobile + desktop. */}
      <div className="border-b border-white/[0.06] px-5 py-3 md:px-12">
        <div className="flex items-center gap-3">
          <CardFaceToggle face={cardFace} onChange={setCardFace} />
          <span aria-hidden className="h-5 w-px bg-white/10" />
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className={cn(
              'inline-flex items-center gap-1.5 border px-3 py-1.5',
              'font-display text-[10px] font-bold uppercase tracking-widest',
              'transition-[border-color,color] duration-fast ease-signal',
              activeFilterCount > 0
                ? 'border-pink text-pink shadow-glow-soft'
                : 'border-white/15 text-text-secondary hover:border-pink/40 hover:text-text-primary',
            )}
          >
            <Icon name="tune" size={12} />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 text-pink">· {activeFilterCount}</span>
            )}
          </button>
          <span className="ml-auto inline-flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            <Icon name="my_location" size={11} />
            {filters.distanceKm} km · Melbourne
          </span>
        </div>
      </div>

      {/* MOBILE — TikTok-style swipe feed */}
      {loading && profiles.length === 0 ? null : items.length === 0 ? (
        <EmptyState
          vacant={profiles.length === 0}
          onReset={() => setFilters(DEFAULT_FILTERS)}
        />
      ) : (
        <>
          <MobileSwipeFeed items={items} />

          {/* DESKTOP — mixed grid */}
          <section className="hidden px-12 py-10 md:block">
            <div
              className={cn(
                'grid gap-6',
                'md:grid-cols-2 xl:grid-cols-3 xl:gap-8',
              )}
            >
              {items.map((item, i) => (
                <FeedItemCard
                  key={item.id}
                  item={item}
                  index={i}
                  defaultFace={cardFace}
                />
              ))}
            </div>
            <p className="mt-14 text-center font-display text-[9px] font-bold uppercase tracking-widest text-text-muted">
              The room refreshes every hour<span className="text-pink">.</span>
            </p>
          </section>
        </>
      )}

      <FilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={filters}
        onApply={(next) => {
          setFilters(next);
          capture('filter_apply', 'feed', undefined, {
            distanceKm: next.distanceKm,
            presence: next.presence,
            bands: next.bands.join(','),
          });
        }}
      />
    </PageShell>
  );
};

const CardFaceToggle = ({
  face,
  onChange,
}: {
  face: CardFace;
  onChange: (f: CardFace) => void;
}) => (
  <div
    role="radiogroup"
    aria-label="Card front"
    className="inline-flex border border-white/[0.10]"
  >
    {(
      [
        { id: 'photo' as const, icon: 'image', label: 'Photo' },
        { id: 'stats' as const, icon: 'list_alt', label: 'Stats' },
      ]
    ).map((opt) => (
      <button
        key={opt.id}
        type="button"
        role="radio"
        aria-checked={face === opt.id}
        onClick={() => onChange(opt.id)}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5',
          'font-display text-[10px] font-bold uppercase tracking-widest',
          'transition-[background,color] duration-fast ease-signal',
          'border-r border-white/[0.10] last:border-r-0',
          face === opt.id
            ? 'bg-pink/10 text-pink shadow-glow-soft'
            : 'text-text-secondary hover:text-text-primary',
        )}
      >
        <Icon name={opt.icon} size={12} />
        {opt.label}
      </button>
    ))}
  </div>
);

const EmptyState = ({
  vacant,
  onReset,
}: {
  vacant: boolean;
  onReset: () => void;
}) => (
  <div className="m-5 border border-white/[0.07] px-6 py-16 text-center md:m-12">
    <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
      {vacant ? '— Nobody here yet' : '— Nothing matches'}
    </p>
    <p className="mt-3 font-display text-2xl font-black uppercase tracking-display">
      {vacant ? 'The room is empty' : 'The room is too quiet'}
      <span className="text-pink">.</span>
    </p>
    <p className="mt-2 text-sm text-text-secondary">
      {vacant
        ? 'Real people only. Come back later — or be first through the door.'
        : 'Loosen a filter and try again.'}
    </p>
    {!vacant && (
      <button
        type="button"
        onClick={onReset}
        className="mt-5 border border-pink px-4 py-2 font-display text-[10px] font-bold uppercase tracking-widest text-pink shadow-glow-soft"
      >
        Reset filters
      </button>
    )}
  </div>
);
